import React, { useEffect } from "react";
import { useCustomSocket } from "./socketContext";

const WebRTCContext = React.createContext(null);

const initialState = {
  peerConnection: null,
  dataChannel: null,
  status: "idle",
  localStream: null,
  remoteStream: null,
  isCaller: false,
  pendingOffer: null,
  pendingAnswer: null,
  identifier: 0,
};

export const useWebRTC = () => {
  const context = React.useContext(WebRTCContext);
  if (context === undefined) {
    throw new Error("useWebRTC must be used within a WebRTCProvider");
  }
  return context;
};

export const WebRTCProvider = ({ children }) => {
  const { socketState, sendMessage, subscribeToEvent } = useCustomSocket();
  const mediaDevices = navigator.mediaDevices;
  const configuration = {
    iceServers: [
      {
        urls: "stun:stun.l.google.com:19302",
      },
      {
        urls: "stun:stun1.l.google.com:19302",
      },
      {
        urls: "stun:stun2.l.google.com:19302",
      },
    ],
  };
  const [webRTCState, setWebRTCState] = React.useState(initialState);

  const handleIceCandidateEvent = async (event) => {
    if (event.candidate) {
      console.log("Sending ICE candidate:", event.candidate);

      socketState.socket?.emit("sendIceCandidateToSignallingServer", {
        iceCandidate: event?.candidate,
        iceCandidateUserName: socketState?.userName,
        isCaller: webRTCState?.isCaller,
      });
      // sendMessage("rtc-signalling", {
      //   type: "ice-candidate",
      //   payload: event?.candidate,
      // });
    }
  };

  const handleDataChannelEvent = (event) => {
    console.log("Data channel event:", JSON.stringify(event));
    // if (webRTCState?.isCaller) return;
    setWebRTCState((prevState) => ({
      ...prevState,
      dataChannel: event?.channel,
    }));
  };

  const handleConnectionStateChangeEvent = (event) => {
    console.log("Connection state changed:", event);
  };

  const subscribeToPeerEvents = (event, cb) => {
    if (!webRTCState.peerConnection) return;
    console.log("Subscribing to peer events: " + event);
    webRTCState.peerConnection[event] = cb;
  };

  const subsribeToDataChannelEvents = (event, cb) => {
    if (!webRTCState.dataChannel) return;
    console.log("Subscribing to data channel events " + event);
    webRTCState.dataChannel[event] = cb;
  };

  const sendViaDataChannel = (message) => {
    if (webRTCState?.dataChannel?.readyState !== "open") return;
    console.log(
      webRTCState?.identifier + "Sending message via data channel:",
      message
    );
    webRTCState.dataChannel?.send(message);
  };

  const handleOffer = async (offer) => {
    if (!webRTCState.peerConnection) return;
    if (webRTCState?.isCaller) return;
    console.log("Received offer:", JSON.stringify(offer));
    try {
      await webRTCState.peerConnection?.setRemoteDescription(
        new RTCSessionDescription(offer)
      );
      if (webRTCState?.pendingOffer) return;
      const answer = await webRTCState.peerConnection?.createAnswer();
      await webRTCState.peerConnection?.setLocalDescription(answer);
      sendMessage("rtc-signalling", { type: "answer", payload: answer });
      setWebRTCState((prevState) => ({ ...prevState, pendingAnswer: answer }));
    } catch (error) {
      console.error("Error handling offer:", error);
    }
  };

  const handleAnswer = async (answer) => {
    if (!webRTCState.peerConnection) return;
    if (!webRTCState?.isCaller) return;
    console.log("Received answer:", JSON.stringify(answer));
    if (webRTCState.peerConnection?.signalingState !== "have-local-offer") {
      console.error(
        "handleAnswer called in wrong state:",
        webRTCState.peerConnection?.signalingState
      );
      return;
    }

    try {
      await webRTCState.peerConnection?.setRemoteDescription(
        new RTCSessionDescription(answer)
      );
      setWebRTCState((prevState) => ({ ...prevState, pendingAnswer: null }));
    } catch (e) {
      console.error(`webRTCProvider: handleAnswer: ${e}`);
    }
  };

  const handleIceCandidate = async (iceCandidate) => {
    if (!webRTCState.peerConnection) return;
    console.log("Received ICE candidate:", JSON.stringify(iceCandidate));
    try {
      await webRTCState.peerConnection?.addIceCandidate(
        new RTCIceCandidate(iceCandidate)
      );
    } catch (error) {
      console.error("Error adding ICE candidate:", error);
    }
  };

  const initializePeers = async (sessionId = "", dataOnly = false) => {
    console.log("Initializing peers");

    const peerConnection = new RTCPeerConnection(configuration);
    let localStream = null;
    if (!dataOnly) {
      localStream = await openMediaDevices({ video: true, audio: true });
      localStream.getTracks().forEach((track) => {
        peerConnection.addTrack(track, localStream);
      });
    }

    //@ts-ignore
    setWebRTCState((prevState) => ({
      ...prevState,
      peerConnection,
      status: "initialized",
      localStream,
      identifier: sessionId,
    }));
  };

  const openMediaDevices = async (constraints) => {
    return await mediaDevices.getUserMedia(constraints);
  };

  async function getConnectedDevices(type) {
    const devices = await mediaDevices.enumerateDevices();
    return devices?.filter((device) => device?.kind === type);
  }

  const startPeerConnection = async (dataOnly = false) => {
    try {
      let currentPeerConnection = webRTCState?.peerConnection;
      let newLocalStream = null;
      if (!currentPeerConnection) {
        currentPeerConnection = new RTCPeerConnection(configuration);
      }

      if (!dataOnly) {
        newLocalStream = await openMediaDevices({
          video: {
            height: "320",
            width: "240",
          },
          audio: true,
        });

        newLocalStream.getTracks().forEach((track) => {
          currentPeerConnection.addTrack(track, newLocalStream);
        });
      }
      const dataChannel =
        currentPeerConnection?.createDataChannel("myDataChannel");

      const offer = await currentPeerConnection.createOffer();
      await currentPeerConnection.setLocalDescription(offer);
      console.log("Starting peer connection and offer is", offer);
      socketState.socket?.emit("newOffer", offer);
      // sendMessage("rtc-signalling", { type: "offer", payload: offer });
      setWebRTCState((prevState) => ({
        ...prevState,
        peerConnection: currentPeerConnection,
        localStream: newLocalStream ? newLocalStream : prevState.localStream,
        dataChannel,
        status: "initialized",
        isCaller: true,
        pendingOffer: offer,
      }));
    } catch (e) {
      console.error(`webRTCProvider: initiatePeerConnection: ${e}`);
    }
  };

  const handleRTCSignalling = (data) => {
    switch (data.type) {
      case "offer":
        handleOffer(data.payload);
        break;
      case "answer":
        handleAnswer(data.payload);
        break;
      case "ice-candidate":
        handleIceCandidate(data.payload);
        break;
      default:
        console.error("Unknown RTC signalling data type:", data.type);
    }
  };

  const closeWebRTCConnection = () => {
    if (!webRTCState.peerConnection || !webRTCState.dataChannel) return;

    console.log("Closing WebRTC connection");
    webRTCState.peerConnection.close();
    webRTCState.dataChannel.close();
    setWebRTCState((prevState) => ({
      ...prevState,
      peerConnection: null,
      dataChannel: null,
      status: "idle",
    }));
  };

  const handleOnIceConnectionStateChange = (event) => {
    console.log("ICE connection state changed:", event);
  };

  const handleOnRemoteStreamEvent = (event) => {
    console.log("Remote stream event:", event.streams[0]);
    setWebRTCState((prevState) => ({
      ...prevState,
      remoteStream: event?.streams[0],
    }));
  };

  useEffect(() => {
    if (!webRTCState.peerConnection) return;
    subscribeToPeerEvents("onicecandidate", handleIceCandidateEvent);

    subscribeToPeerEvents("ondatachannel", handleDataChannelEvent);

    subscribeToPeerEvents(
      "onconnectionstatechange",
      handleConnectionStateChangeEvent
    );

    subscribeToPeerEvents(
      "oniceconnectionstatechange",
      handleOnIceConnectionStateChange
    );

    subscribeToPeerEvents("ontrack", handleOnRemoteStreamEvent);

    return () => {};
  }, [webRTCState?.peerConnection]);

  useEffect(() => {
    if (!webRTCState.dataChannel) return;
    subsribeToDataChannelEvents("onopen", () => {
      console.log("Data channel is open");
    });
    subsribeToDataChannelEvents("onmessage", (event) => {
      console.log(
        webRTCState?.identifier + "Data channel message of event",
        event
      );
    });
    subsribeToDataChannelEvents("onclose", () => {
      console.log("Data channel is close");
    });
  }, [webRTCState?.dataChannel]);

  useEffect(() => {
    if (!socketState.socket) return;

    // subscribeToEvent("offer", handleOffer);
    // subscribeToEvent("answer", handleAnswer);
    // subscribeToEvent("ice-candidate", handleIceCandidate);
    subscribeToEvent("rtc-signalling", handleRTCSignalling);
  }, [socketState?.socket, webRTCState?.peerConnection, webRTCState?.isCaller]);

  return (
    <WebRTCContext.Provider
      value={{
        webRTCState,
        initializePeers,
        startPeerConnection,
        subsribeToDataChannelEvents,
        subscribeToPeerEvents,
        closeWebRTCConnection,
        sendViaDataChannel,
      }}
    >
      {children}
    </WebRTCContext.Provider>
  );
};

// import React, { useEffect } from "react";
// import { useCustomSocket } from "./socketContext";

// const WebRTCContext = React.createContext(null);
// export const useWebRTC = () => {
//   const context = React.useContext(WebRTCContext);
//   if (context === undefined) {
//     throw new Error("useWebRTC must be used within a WebRTCProvider");
//   }
//   return context;
// };

// export const WebRTCProvider = ({ children }) => {
//   const { socketState, sendMessage, subscribeToEvent } = useCustomSocket();
//   const configuration = {
//     iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
//   };
//   const [webRTCState, setWebRTCState] = React.useState({
//     peerConnection: null,
//     dataChannel: null,
//     status: "idle",
//     isCaller: false,
//     callerOffer: null,
//     calleeOffer: null,
//     callerIceCandidates: [],
//     calleeIceCandidates: [],
//   });

//   const handleIceCandidateEvent = async (event) => {
//     if (event.candidate) {
//       console.log("Sending ICE candidate:", event.candidate);
//       sendMessage("rtc-signalling", {
//         type: "ice-candidate",
//         payload: event?.candidate,
//       });
//     }
//   };

//   const handleDataChannelEvent = (event) => {
//     console.log("Data channel event:", event);
//     setWebRTCState((prevState) => ({
//       ...prevState,
//       dataChannel: event?.channel,
//     }));
//   };

//   const handleConnectionStateChangeEvent = (event) => {
//     console.log("Connection state changed:", event?.target?.connectionState);
//     if (
//       event.target.connectionState === "failed" ||
//       event.target.connectionState === "disconnected"
//     ) {
//       setWebRTCState((prevState) => ({
//         ...prevState,
//         status: "error",
//       }));
//     }
//   };

//   const subscribeToPeerEvents = (event, cb) => {
//     if (!webRTCState.peerConnection) return;
//     console.log("Subscribing to peer events: " + event);
//     webRTCState.peerConnection[event] = cb;
//   };

//   const subsribeToDataChannelEvents = (event, cb) => {
//     if (!webRTCState.dataChannel) return;
//     console.log("Subscribing to data channel events: " + event);
//     webRTCState.dataChannel[event] = cb;
//   };

//   const sendViaDataChannel = (message) => {
//     if (!webRTCState.dataChannel) return;
//     webRTCState.dataChannel?.send(message);
//   };

//   const handleOffer = async (offer) => {
//     if (!webRTCState.peerConnection) return;
//     console.log("Received offer:", offer);

//     try {
//       await webRTCState.peerConnection?.setRemoteDescription(
//         new RTCSessionDescription(offer)
//       );
//       const answer = await webRTCState.peerConnection?.createAnswer();
//       await webRTCState.peerConnection?.setLocalDescription(answer);
//       sendMessage("rtc-signalling", { type: "answer", payload: answer });
//     } catch (error) {
//       console.error("Error handling offer:", error);
//     }
//   };

//   const handleAnswer = async (answer) => {
//     if (!webRTCState.peerConnection) return;
//     if (webRTCState.peerConnection?.signalingState !== "have-local-offer") {
//       console.error(
//         "handleAnswer called in wrong state:",
//         webRTCState.peerConnection.signalingState
//       );
//       return;
//     }
//     console.log(
//       "Received answer:",
//       answer,
//       webRTCState.peerConnection?.signalingState
//     );

//     try {
//       await webRTCState.peerConnection?.setRemoteDescription(
//         new RTCSessionDescription(answer)
//       );
//     } catch (e) {
//       console.error(`webRTCProvider: handleAnswer: ${e}`);
//     }
//   };

//   const handleIceCandidate = async (iceCandidate) => {
//     if (!webRTCState.peerConnection) return;
//     console.log("Received ICE candidate:", iceCandidate);
//     try {
//       await webRTCState.peerConnection?.addIceCandidate(
//         new RTCIceCandidate(iceCandidate)
//       );
//     } catch (error) {
//       console.error("Error adding ICE candidate:", error);
//     }
//   };

//   const initializePeers = () => {
//     console.log("Initializing peers");
//     const peerConnection = new RTCPeerConnection(configuration);
//     setWebRTCState((prevState) => ({
//       peerConnection,
//       dataChannel: null,
//       status: "initialized",
//     }));
//   };

//   const startPeerConnection = async () => {
//     try {
//       let currentPeerConnection = webRTCState.peerConnection;

//       if (!currentPeerConnection) {
//         currentPeerConnection = new RTCPeerConnection(configuration);
//       }
//       const dataChannel =
//         currentPeerConnection.createDataChannel("myDataChannel");
//       setWebRTCState((prevState) => ({
//         ...prevState,
//         peerConnection: currentPeerConnection,
//         dataChannel,
//         status: "initialized",
//       }));
//       const offer = await currentPeerConnection.createOffer(null);
//       await currentPeerConnection.setLocalDescription(offer);
//       console.log("Starting peer connection and offer is", offer);
//       sendMessage("rtc-signalling", {
//         type: "offer",
//         payload: offer,
//       });
//     } catch (e) {
//       console.error(`webRTCProvider: initiatePeerConnection: ${e}`);
//     }
//   };

//   const handleRTCSignalling = (data) => {
//     switch (data.type) {
//       case "offer":
//         handleOffer(data.payload);
//         break;
//       case "answer":
//         handleAnswer(data.payload);
//         break;
//       case "ice-candidate":
//         handleIceCandidate(data.payload);
//         break;
//       default:
//         console.error("Unknown RTC signalling data type:", data.type);
//     }
//   };

//   const closeWebRTCConnection = () => {
//     if (!webRTCState.peerConnection || !webRTCState.dataChannel) return;

//     console.log("Closing WebRTC connection");
//     webRTCState.peerConnection.close();
//     webRTCState.dataChannel.close();
//     setWebRTCState((prevState) => ({
//       ...prevState,
//       peerConnection: null,
//       dataChannel: null,
//       status: "idle",
//     }));
//   };

//   const handleOnIceConnectionStateChange = (event) => {
//     console.log(
//       "ICE connection state changed:",
//       event?.target?.iceConnectionState
//     );

//     if (event?.target?.iceConnectionState === "failed") {
//       setWebRTCState((prevState) => ({
//         ...prevState,
//         status: "error",
//       }));
//     }
//   };

//   useEffect(() => {
//     if (!webRTCState.peerConnection) return;
//     subscribeToPeerEvents("onicecandidate", handleIceCandidateEvent);

//     subscribeToPeerEvents("ondatachannel", handleDataChannelEvent);

//     subscribeToPeerEvents(
//       "onconnectionstatechange",
//       handleConnectionStateChangeEvent
//     );

//     subscribeToPeerEvents(
//       "oniceconnectionstatechange",
//       handleOnIceConnectionStateChange
//     );
//   }, [webRTCState?.peerConnection]);

//   useEffect(() => {
//     if (!socketState.socket) return;
//     subscribeToEvent("rtc-signalling", handleRTCSignalling);
//   }, [socketState?.socket, webRTCState?.peerConnection]);

//   useEffect(() => {
//     if (!webRTCState.dataChannel) return;
//     subsribeToDataChannelEvents("onopen", () => {
//       console.log("Data channel is open");
//     });
//     // subsribeToDataChannelEvents("onmessage", (event) => {
//     //   console.log("Data channel message of event", event);
//     // });
//     subsribeToDataChannelEvents("onclose", () => {
//       console.log("Data channel is close");
//     });

//     return () => {};
//   }, [webRTCState?.dataChannel]);

//   return (
//     <WebRTCContext.Provider
//       value={{
//         webRTCState,
//         initializePeers,
//         startPeerConnection,
//         subsribeToDataChannelEvents,
//         subscribeToPeerEvents,
//         closeWebRTCConnection,
//         sendViaDataChannel,
//       }}
//     >
//       {children}
//     </WebRTCContext.Provider>
//   );
// };
